import { createSignedUploadUrl } from '../api/adminApi';

export const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;

export const IMAGE_MIME_TYPES = Object.freeze([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
]);

export class UploadError extends Error {
  constructor(message, { code, cause } = {}) {
    super(message || 'Upload failed');
    this.name = 'UploadError';
    if (code) this.code = code;
    if (cause) this.cause = cause;
  }
}

function basenameWithoutExt(name) {
  const idx = name.lastIndexOf('.');
  return idx === -1 ? name : name.slice(0, idx);
}

function extFromName(name) {
  const idx = name.lastIndexOf('.');
  return idx === -1 ? '' : name.slice(idx).toLowerCase();
}

function extFromMime(mime) {
  switch ((mime || '').toLowerCase()) {
    case 'image/png':
      return '.png';
    case 'image/jpeg':
    case 'image/jpg':
      return '.jpg';
    case 'image/webp':
      return '.webp';
    case 'image/gif':
      return '.gif';
    default:
      return '';
  }
}

/**
 * Make a filename safe for storage: lowercase, dashes only, capped length.
 * Always falls back to a generic `upload` if the input is empty after sanitizing.
 */
export function sanitizeFileName(rawName, fallbackMime) {
  const original = String(rawName || '').trim();
  const ext = extFromName(original) || extFromMime(fallbackMime);
  const base = basenameWithoutExt(original)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s.-]+/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[.-]+|[.-]+$/g, '')
    .slice(0, 80);
  const safeBase = base || 'upload';
  return `${safeBase}${ext}`;
}

/**
 * @param {number} bytes
 * @returns {string}
 */
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
}

/**
 * Validate a File against accepted MIME types and max byte count.
 * Throws UploadError when invalid.
 */
export function validateFile(file, { accept = IMAGE_MIME_TYPES, maxBytes = DEFAULT_MAX_BYTES } = {}) {
  if (!file) throw new UploadError('No file selected.');
  if (accept && accept.length && !accept.includes(file.type)) {
    throw new UploadError(
      `Unsupported file type${file.type ? ` (${file.type})` : ''}. Allowed: ${accept.join(', ')}.`,
      { code: 'UNSUPPORTED_TYPE' },
    );
  }
  if (Number.isFinite(maxBytes) && file.size > maxBytes) {
    throw new UploadError(
      `File is too large (${formatBytes(file.size)}). Max ${formatBytes(maxBytes)}.`,
      { code: 'TOO_LARGE' },
    );
  }
}

/**
 * Ask the backend for a v4 signed PUT URL.
 * Returns the `data` payload (uploadUrl, publicUrl, objectKey, headers, ...).
 */
export async function requestSignedUploadUrl({ folder, file, fileName }) {
  if (!folder) throw new UploadError('Upload folder is required.');
  if (!file) throw new UploadError('No file provided for signed URL.');
  const safeName = sanitizeFileName(fileName || file.name, file.type);
  return createSignedUploadUrl({
    folder,
    fileName: safeName,
    contentType: file.type,
    fileSize: file.size,
  });
}

/**
 * PUT a File to a signed GCS URL using XHR so we can report progress.
 * `headers` should be the object returned by the backend (Content-Type + x-goog-*).
 */
const CORS_HINT =
  'If the console shows a CORS error, the GCS bucket must allow your exact admin origin (scheme + host + port), e.g. http://localhost:3000 is not the same as http://127.0.0.1:3000. Clear site data or disable cache and retry after updating bucket CORS.';

export function putFileToSignedUrl(uploadUrl, file, headers = {}, onProgress) {
  return new Promise((resolve, reject) => {
    let settled = false;
    function finish(fn) {
      if (settled) return;
      settled = true;
      fn();
    }

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);

    Object.entries(headers).forEach(([key, value]) => {
      if (value != null) xhr.setRequestHeader(key, String(value));
    });

    if (typeof onProgress === 'function') {
      xhr.upload.addEventListener('progress', (evt) => {
        if (!evt.lengthComputable) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        onProgress(pct, evt);
      });
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (typeof onProgress === 'function') onProgress(100);
        finish(() => resolve());
        return;
      }
      if (xhr.status === 0) {
        finish(() =>
          reject(
            new UploadError(
              `Upload blocked or failed before a response (HTTP 0). ${CORS_HINT}`,
              { code: 'CORS_OR_BLOCKED' },
            ),
          ),
        );
        return;
      }
      finish(() =>
        reject(
          new UploadError(
            `Upload failed (HTTP ${xhr.status}). ${xhr.responseText || ''}`.trim(),
            { code: 'GCS_PUT_FAILED' },
          ),
        ),
      );
    };
    xhr.onerror = () =>
      finish(() =>
        reject(
          new UploadError(
            `Network error during upload (browser may have blocked the cross-origin PUT). ${CORS_HINT}`,
            { code: 'NETWORK' },
          ),
        ),
      );
    xhr.onabort = () => finish(() => reject(new UploadError('Upload aborted.', { code: 'ABORTED' })));

    xhr.send(file);
  });
}

/**
 * High-level helper: validate, request signed URL, PUT the file, return public info.
 *
 * @param {object} opts
 * @param {File}   opts.file
 * @param {string} opts.folder            Storage folder, e.g. "menu-items"
 * @param {string} [opts.fileName]        Override the source file name
 * @param {string[]} [opts.accept]        Allowed MIME types
 * @param {number} [opts.maxBytes]        Max bytes (client-side guard)
 * @param {(pct:number)=>void} [opts.onProgress]
 * @returns {Promise<{publicUrl:string, objectKey:string, uploadUrl:string, expiresAt:string}>}
 */
export async function uploadFile({
  file,
  folder,
  fileName,
  accept = IMAGE_MIME_TYPES,
  maxBytes = DEFAULT_MAX_BYTES,
  onProgress,
}) {
  validateFile(file, { accept, maxBytes });
  const signed = await requestSignedUploadUrl({ folder, file, fileName });
  if (!signed?.uploadUrl) {
    throw new UploadError('Server did not return an upload URL.', { code: 'NO_UPLOAD_URL' });
  }
  await putFileToSignedUrl(signed.uploadUrl, file, signed.headers || {}, onProgress);
  return {
    publicUrl: signed.publicUrl,
    objectKey: signed.objectKey,
    uploadUrl: signed.uploadUrl,
    expiresAt: signed.expiresAt,
  };
}

/** Load an Image element from a URL (object URL or remote URL). */
export function loadImageElement(src, { crossOrigin } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new UploadError('Failed to load image for cropping.', { code: 'IMAGE_LOAD' }));
    img.src = src;
  });
}

/**
 * Crop an image to the given pixel rectangle and return a Blob.
 *
 * @param {object} opts
 * @param {string} opts.imageSrc       Source URL (object URL or http) for the original image.
 * @param {{x:number,y:number,width:number,height:number}} opts.cropPixels
 * @param {{width:number,height:number}} [opts.outputSize]  Resize to these dimensions; defaults to crop size.
 * @param {string} [opts.outputType]   'image/jpeg' | 'image/png' | 'image/webp'. Default 'image/jpeg'.
 * @param {number} [opts.quality]      0..1, used for JPEG/WebP. Default 0.92.
 * @returns {Promise<Blob>}
 */
export async function cropImageToBlob({
  imageSrc,
  cropPixels,
  outputSize,
  outputType = 'image/jpeg',
  quality = 0.92,
}) {
  if (!cropPixels) throw new UploadError('Missing crop rectangle.', { code: 'NO_CROP' });
  const img = await loadImageElement(imageSrc);
  const targetW = Math.max(1, Math.round(outputSize?.width || cropPixels.width));
  const targetH = Math.max(1, Math.round(outputSize?.height || cropPixels.height));

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new UploadError('Canvas unavailable in this browser.', { code: 'NO_CANVAS' });

  if (outputType === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetW, targetH);
  }

  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    img,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    targetW,
    targetH,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new UploadError('Failed to encode cropped image.', { code: 'ENCODE_FAILED' }));
      },
      outputType,
      quality,
    );
  });
}

/** Read a File as a data/object URL suitable for showing in <img>. */
export function fileToObjectUrl(file) {
  if (!file) return '';
  return URL.createObjectURL(file);
}

/** Build a File from a Blob with a sensible name + type. */
export function blobToFile(blob, fileName, outputType) {
  const type = outputType || blob.type || 'application/octet-stream';
  const name = sanitizeFileName(fileName || `upload.${type.split('/')[1] || 'bin'}`, type);
  return new File([blob], name, { type, lastModified: Date.now() });
}
